import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { LANG_CODES } from '../lib/langs';

const I18nContext = createContext(null);

function detectLangFromPath(pathname) {
  const seg = pathname.split('/')[1];
  if (LANG_CODES.includes(seg) && seg !== 'en') return seg;
  return 'en';
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLangState] = useState(() => {
    const fromUrl = detectLangFromPath(location.pathname);
    if (fromUrl !== 'en') return fromUrl;
    return localStorage.getItem('esi_lang') || 'en';
  });
  const [strings, setStrings] = useState({});

  // Sync lang with URL prefix
  useEffect(() => {
    const urlLang = detectLangFromPath(location.pathname);
    if (urlLang !== lang) setLangState(urlLang);
  }, [location.pathname]);

  // Fetch translations when lang changes
  useEffect(() => {
    localStorage.setItem('esi_lang', lang);
    if (lang === 'en') { setStrings({}); return; }
    api.get(`/i18n/${lang}`).then((r) => setStrings(r.data || {})).catch(() => setStrings({}));
  }, [lang]);

  const setLang = (newLang) => {
    if (!LANG_CODES.includes(newLang)) return;
    // Rewrite URL: strip current lang prefix, add new one
    let path = location.pathname;
    const seg = path.split('/')[1];
    if (LANG_CODES.includes(seg) && seg !== 'en') {
      path = '/' + path.split('/').slice(2).join('/');
    }
    if (!path.startsWith('/')) path = '/' + path;
    const prefix = newLang === 'en' ? '' : `/${newLang}`;
    navigate(prefix + path + location.search + location.hash);
    setLangState(newLang);
  };

  const t = useMemo(() => (key, fallback) => strings[key] || fallback || key, [strings]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, strings }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) return { lang: 'en', setLang: () => {}, t: (k, f) => f || k, strings: {} };
  return ctx;
};
